import { motion } from "framer-motion";
import { PIZZE, ALTRO, DRINKS, NOTES } from "../data/menu";
import { MaskText, FadeIn } from "./Reveal";

const Row = ({ item }) => (
  <motion.div
    data-testid={`menu-item-${item.name}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
    className="group grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 items-baseline py-5 border-b border-[var(--line)] transition-[padding] duration-500 hover:pl-3 md:hover:pl-5"
  >
    <div>
      <h4 className="font-serif text-2xl md:text-3xl leading-tight">
        {item.name}
        {item.frozen && <span className="align-super text-[var(--fg-dim)] text-base ml-0.5">*</span>}
      </h4>
      {item.ingredients && (
        <p className="mt-1.5 text-[var(--fg-dim)] text-sm leading-relaxed max-w-xl">
          {item.ingredients}
        </p>
      )}
    </div>
    <span className="font-mono text-sm md:text-base text-right whitespace-nowrap text-[var(--fg)]">
      {item.price.includes("·") || item.price.includes("lt")
        ? item.price
        : `${item.price} €`}
    </span>
  </motion.div>
);

const ChapterHeader = ({ number, title, subtitle }) => (
  <div className="grid md:grid-cols-12 gap-4 md:gap-10 items-end mb-8 md:mb-12">
    <div className="md:col-span-8">
      <span className="font-mono text-sm text-[var(--fg-dim)] block mb-3">{number}</span>
      <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
        <MaskText lines={[title]} />
      </h3>
    </div>
    {subtitle && (
      <FadeIn className="md:col-span-4" delay={0.1}>
        <p className="text-[var(--fg-dim)] text-sm leading-relaxed md:pb-2">{subtitle}</p>
      </FadeIn>
    )}
  </div>
);

const GroupBlock = ({ chapter }) => (
  <div className="mb-24 md:mb-36">
    <ChapterHeader number={chapter.number} title={chapter.title} />
    {chapter.groups.map((g) => (
      <div key={g.label} className="mb-10 md:mb-14">
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--fg-dim)] block mb-2 pt-4 border-t border-[var(--line-strong)]">
          {g.label}
        </span>
        {g.items.map((item) => (
          <Row key={item.name} item={item} />
        ))}
      </div>
    ))}
  </div>
);

export const MenuSection = () => {
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[var(--bg-2)] border-y border-[var(--line)]"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-28 md:py-44">
        {/* intro */}
        <div className="grid md:grid-cols-12 gap-6 mb-24 md:mb-40">
          <div className="md:col-span-3">
            <FadeIn>
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--fg-dim)]">
                — Il Menù
              </span>
            </FadeIn>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-[13vw] md:text-8xl lg:text-[9rem] leading-[0.85] tracking-tighter">
              <MaskText lines={["Le nostre", <span key="i" className="italic font-light">pizze</span>]} />
            </h2>
          </div>
        </div>

        {/* Pizze */}
        {PIZZE.map((chapter) => (
          <div key={chapter.id} className="mb-24 md:mb-36">
            <ChapterHeader
              number={chapter.number}
              title={chapter.title}
              subtitle={chapter.subtitle}
            />
            <div>
              {chapter.items.map((item) => (
                <Row key={item.name} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Burger + Fritti */}
        {ALTRO.map((chapter) => (
          <div key={chapter.id} className="mb-24 md:mb-36">
            <ChapterHeader
              number={chapter.number}
              title={chapter.title}
              subtitle={chapter.subtitle}
            />
            <div>
              {chapter.items.map((item) => (
                <Row key={item.name} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Drinks */}
        {DRINKS.map((chapter) => (
          <GroupBlock key={chapter.id} chapter={chapter} />
        ))}

        {/* Notes */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 pt-10 border-t border-[var(--line-strong)]">
          {NOTES.map((n) => (
            <FadeIn key={n.title}>
              <h4 className="font-serif text-3xl md:text-4xl mb-3">{n.title}</h4>
              <p className="text-[var(--fg-dim)] text-sm md:text-base leading-relaxed">{n.text}</p>
            </FadeIn>
          ))}
        </div>

        <p className="mt-16 font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--fg-dim)]">
          * Prodotto surgelato
        </p>
      </div>
    </section>
  );
};

